/**
 * useNewChatGreeting — restores the returning-user "New Chat" greeting.
 *
 * Regression context: #4598 ("chat-window onboarding + context recipes; retire
 * legacy 5-state machine", commit 4d77715b9) removed the `useOnboardingDriver`
 * hook, whose **auto-greet effect** seeded a welcome message for every new empty
 * conversation. Its replacement, `useChatOnboarding`, only seeds welcome copy
 * while the dedicated first-run flow is active (`onboardingActive`). So an
 * existing, onboarding-complete user clicking "New Chat" got a blank chat until
 * they typed — the very "New Chat renders blank" symptom the #1583 race fix was
 * written to prevent.
 *
 * This hook revives only the regular-chat branch of that deleted effect: an
 * empty, non-onboarding conversation seeds a one-off CLIENT-SIDE greeting
 * (`pickGreeting` — no LLM call, deterministic, instant) via `addAssistantMessage`
 * with `routing: 'greeting'`. The onboarding branch stays owned by
 * `useChatOnboarding`, so this effect stands down whenever `onboardingActive`.
 *
 * The #1583 "New Chat renders blank" race protections are upheld: the effect
 * waits for the message fetch (`isLoading`) and any active stream (`isStreaming`)
 * to settle before seeding, so the greeting is always added AFTER the fetch — it
 * can no longer be wiped by an empty server response (which the functional
 * fetch-merge in `useSessionMessageLoader` also guards). Dedup is a single
 * idempotent `greetedConversationIds.has → add`: re-renders (and StrictMode's
 * synthetic double-invoke) can't double-seed, and ChatBox clears a conversation's
 * entry on `clearChat`, so a cleared chat greets again.
 */
import { useEffect, useRef } from 'react';
import {
  pickGreeting,
  getLastMessageTimestamp,
  getRecentChatCount,
} from '@/lib/greetings';

interface UseNewChatGreetingArgs {
  /** Active conversation id (undefined when none is selected). */
  conversationId: string | undefined;
  /** Message count for the active conversation. */
  messagesLength: number;
  /** True while the session message fetch is in flight. */
  isLoading: boolean;
  /** True while a chat / greeting stream is active. */
  isStreaming: boolean;
  /** True once the signed-in profile has loaded (an authenticated session). */
  hasProfile: boolean;
  /** True while the dedicated onboarding flow owns the welcome copy. */
  onboardingActive: boolean;
  /** Appends + persists an assistant message (from useChat). */
  addAssistantMessage: (
    content: string,
    conversationId?: string,
    metadata?: { routing?: string },
  ) => void;
  /**
   * Module-level set tracking already-greeted conversations. Owned by ChatBox
   * (it also clears entries on `clearChat`) and StrictMode-safe.
   */
  greetedConversationIds: Set<string>;
}

export function useNewChatGreeting({
  conversationId,
  messagesLength,
  isLoading,
  isStreaming,
  hasProfile,
  onboardingActive,
  addAssistantMessage,
  greetedConversationIds,
}: UseNewChatGreetingArgs): void {
  // Stable ref so a fresh `addAssistantMessage` identity doesn't re-fire the
  // effect (matches the original auto-greet effect's ref discipline).
  const addAssistantMessageRef = useRef(addAssistantMessage);
  addAssistantMessageRef.current = addAssistantMessage;

  useEffect(() => {
    // The onboarding recipe seeds its own welcome copy — don't double up.
    if (onboardingActive) return;
    if (!conversationId) return;
    // Temp optimistic ids (`conversation-*`) have no server row yet, so persisting
    // the greeting would 404 and the random pick would change once the real id
    // swaps in. Wait for the real id; `useSessionMessageLoader` bypasses the GET
    // for it (so messages stay []) and the effect re-runs to greet exactly once.
    if (conversationId.startsWith('conversation-')) return;
    if (messagesLength > 0) return;
    // Wait for the message fetch to finish — otherwise an empty server response
    // wipes the greeting we just added (#1583).
    if (isLoading) return;
    if (isStreaming) return;
    // `profile` is null mid-fetch; greeting then would address an unauthenticated
    // shell before the session resolves.
    if (!hasProfile) return;

    // Idempotent dedup: greet each conversation at most once per session. This is
    // StrictMode-safe (the synthetic re-invoke sees the id already present) and
    // coordinates with ChatBox's `clearChat`, which deletes the id so a cleared
    // chat greets again.
    if (greetedConversationIds.has(conversationId)) return;
    greetedConversationIds.add(conversationId);

    const greeting = pickGreeting({
      lastMessageAt: getLastMessageTimestamp(),
      recentChatCount: getRecentChatCount(),
    });
    addAssistantMessageRef.current(greeting, conversationId, { routing: 'greeting' });
  }, [
    conversationId,
    messagesLength,
    isLoading,
    isStreaming,
    hasProfile,
    onboardingActive,
    // Stable module-level Set — its reference never changes, so this entry never
    // re-fires the effect on its own (it's listed only to satisfy exhaustive-deps).
    // The effect re-evaluates via conversationId / messagesLength; the Set is read
    // synchronously inside, so its mutations are always seen.
    greetedConversationIds,
  ]);
}
